import React, { useState, useEffect, useRef, useContext } from "react";
import api from "../../../../../../../services/api";

import BootstrapTable from "react-bootstrap-table-next";
import ToolkitProvider from "react-bootstrap-table2-toolkit";
import Select from "react-select";
import NotificationAlert from "react-notification-alert";
import { Button, FormGroup, Modal, Row, Col } from "reactstrap";
import EmpresaContext from "../../../../../../../contexts/Empresa";
import { Empresa, Equipe } from "../../../../../../../entities/Common";
import "./styles.css";

interface EquipeSelecionada {
  value: string | number;
  label: string;
}

interface AddEquipeFormModalProps {
  close: () => void;
  show: boolean;
  formId: string | number;
  formName?: string;
}

export default function AddEquipeFormModal({
  close,
  show,
  formId,
  formName,
}: AddEquipeFormModalProps) {
  const { empresaSelecionada } = useContext(EmpresaContext);
  const [equipesDaForm, setEquipesDaForm] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [equipeSelecionada, setEquipeSelecionada] =
    useState<EquipeSelecionada | null>(null);
  const [empresasConfiguraveis, setEmpresasConfiguraveis] = useState<
    Empresa[]
  >([]);
  const [empresaConfiguracaoId, setEmpresaConfiguracaoId] = useState<
    number | null
  >(null);
  const [equipes, setEquipes] = useState<Equipe[]>([]);
  const [funis, setFunis] = useState<any[]>([]);
  const [funilId, setFunilId] = useState<number | null>(null);
  const [distribuirLeads, setDistribuirLeads] = useState(false);
  const notificationAlert = useRef<NotificationAlert>(null);
  const empresaSelecionadaId = empresaSelecionada?.id;

  // A empresa selecionada só é um grupo quando tem filiais configuráveis
  const isEmpresaGrupo = empresasConfiguraveis.length > 1;
  // A distribuição é uma configuração da campanha do grupo, não das filiais
  const podeConfigurarDistribuicao =
    isEmpresaGrupo &&
    Number(empresaConfiguracaoId) === Number(empresaSelecionada?.id);

  const [columns] = useState([
    {
      dataField: "acoes",
      formatter: (cell: any, row: any) => acoesFormatter(cell, row),
    },
    {
      dataField: "nome",
      text: "Nome",
      sort: true,
    },
  ]);

  async function loadEmpresasConfiguraveis() {
    if (!empresaSelecionada?.id) return;

    try {
      const { data } = await api.get<Empresa[]>(
        `/common/empresas/${empresaSelecionada.id}/empresas-grupo`,
      );
      setEmpresasConfiguraveis([empresaSelecionada, ...(data || [])]);
    } catch (error) {
      setEmpresasConfiguraveis([empresaSelecionada]);
    }
    setEmpresaConfiguracaoId(empresaSelecionada.id);
  }

  async function loadEquipes(empresaId: number) {
    try {
      const { data } = await api.get<Equipe[]>(
        `/common/empresas/${empresaId}/equipes`,
      );
      const equipesCaptadoras = data.filter(
        // @ts-ignore
        (equipe) => equipe.captacao === "A" || equipe.captadora === "A"
      );
      setEquipes(equipesCaptadoras);
    } catch (error) {
      notify("danger", "Não foi possível carregar equipes");
    }
  }

  async function loadEquipesDaForm(id: string, empresaId: number) {
    try {
      setIsLoading(true);
      const { data } = await api.get(
        `/meta/forms-equipes/${id}?empresa_id=${empresaId}`,
      );
      setEquipesDaForm(data);
    } catch (error) {
    } finally {
      setIsLoading(false);
    }
  }

  async function loadFunis(empresaId: number) {
    try {
      const { data } = await api.get(
        `/common/empresas/${empresaId}/funis?tipo=crm&situacao=A`,
      );
      setFunis(data || []);
    } catch (error) {
      notify("danger", "Não foi possível carregar funis");
    }
  }

  async function loadFunilDaForm(id: string, empresaId: number) {
    try {
      const { data } = await api.get(
        `/meta/forms/${id}?empresa_id=${empresaId}`,
      );
      setFunilId(data?.funil_id ?? null);
      setDistribuirLeads(Boolean(data?.distribuir_leads));
    } catch (error) {
      setFunilId(null);
      setDistribuirLeads(false);
    }
  }

  async function alterarFunilDaForm(value: number | null) {
    if (!empresaConfiguracaoId) return;
    const previous = funilId;
    setFunilId(value); // atualização otimista
    try {
      await api.put(`/meta/forms/${formId}/funil`, {
        funil_id: value,
        empresa_id: empresaConfiguracaoId,
      });
      notify("success", value ? "Funil vinculado." : "Funil removido.");
    } catch (error) {
      setFunilId(previous); // reverte em caso de falha
      notify("danger", "Não foi possível atualizar o funil");
    }
  }

  async function alterarDistribuicaoDaForm(value: boolean) {
    if (!empresaConfiguracaoId) return;
    const previous = distribuirLeads;
    setDistribuirLeads(value); // atualização otimista
    try {
      await api.put(`/meta/forms/${formId}/distribuicao`, {
        distribuir_leads: value,
        empresa_id: empresaConfiguracaoId,
      });
      notify(
        "success",
        value
          ? "Distribuição de leads ativada para este formulário."
          : "Distribuição de leads desativada para este formulário.",
      );
    } catch (error: any) {
      setDistribuirLeads(previous); // reverte em caso de falha
      notify(
        "danger",
        error?.response?.data?.message ||
          "Não foi possível atualizar a distribuição de leads",
      );
    }
  }

  useEffect(() => {
    if (!show) return;
    loadEmpresasConfiguraveis();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show, empresaSelecionadaId]);

  useEffect(() => {
    if (!show || !formId || !empresaConfiguracaoId) return;

    setEquipeSelecionada(null);
    setEquipes([]);
    setEquipesDaForm([]);
    setFunis([]);
    setFunilId(null);
    setDistribuirLeads(false);
    loadEquipes(empresaConfiguracaoId);
    loadEquipesDaForm(formId.toString(), empresaConfiguracaoId);
    loadFunis(empresaConfiguracaoId);
    loadFunilDaForm(formId.toString(), empresaConfiguracaoId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show, formId, empresaConfiguracaoId]);

  const equipeOptions = equipes?.filter(
    (eq) =>
      !equipesDaForm.find(
        // @ts-ignore
        (eqa) => String(eqa.equipe_captacao_id) === String(eq.id),
      ),
  );

  async function removerEquipeDeForm(equipe: any) {
    try {
      setIsLoading(true);
      await api.delete(
        `/meta/forms-equipes/${equipe.form_id}/ec/${equipe.equipe_captacao_id}`,
        { params: { empresa_id: empresaConfiguracaoId } },
      );
      loadEquipesDaForm(equipe.form_id, empresaConfiguracaoId as number);
      notify("success", "Equipe removida.");
    } catch (error) {
      notify("danger", "Não foi possível remover equipe");
    }
    setIsLoading(false);
  }

  async function addEquipeToForm() {
    if (!equipeSelecionada || !empresaConfiguracaoId) return;
    try {
      setIsLoading(true);
      await api.post("/meta/forms-equipes", {
        form_id: formId,
        equipe_captacao_id: equipeSelecionada.value,
        empresa_id: empresaConfiguracaoId,
      });
      setEquipeSelecionada(null);
      loadEquipesDaForm(formId.toString(), empresaConfiguracaoId);
      notify("success", "Equipe adicionada.");
    } catch (error) {
      notify("danger", "Não foi possível adicionar equipe");
    } finally {
      setIsLoading(false);
    }
  }

  function acoesFormatter(_row: any, cell: any) {
    return (
      <div className="btn-group" role="group">
        <Button
          className="btn-sm"
          color="danger"
          onClick={() => removerEquipeDeForm(cell)}
        >
          <i className="fas fa-trash"></i>
        </Button>
      </div>
    );
  }

  function notify(type: string, msg: string) {
    let options = {
      place: "tc",
      message: (
        <div className="alert-text">
          <span data-notify="message">{msg}</span>
        </div>
      ),
      type: type,
      icon: "ni ni-bell-55",
      autoDismiss: 7,
    };
    if (notificationAlert?.current)
      //@ts-ignore
      notificationAlert.current.notificationAlert(options);
  }

  const customStyles = {
    container: (provided: any) => ({ ...provided, flex: 1 }),
    option: (provided: any) => ({ ...provided, zIndex: 100, fontSize: "14px" }),
    menu: (provided: any) => ({ ...provided, zIndex: 100 }),
    control: (provided: any) => ({
      ...provided,
      minHeight: "36px",
      fontSize: "14px",
    }),
  };

  return (
    <>
      <div className="rna-wrapper">
        <NotificationAlert ref={notificationAlert} />
      </div>

      <Modal
        className="modal-dialog-centered"
        size="md"
        isOpen={show}
        toggle={close}
      >
        <div className="modal-header">
          <h5 className="modal-title">
            Equipe e Funil — {formName || formId}
          </h5>
          <button
            aria-label="Close"
            className="close"
            data-dismiss="modal"
            type="button"
            onClick={close}
          >
            <span aria-hidden={true}>×</span>
          </button>
        </div>
        <div className="modal-body">
          <Row>
            <Col
              lg={12}
              md={12}
              style={{
                flexDirection: "column",
                alignItems: "center",
                flex: 1,
                display: "flex",
              }}
            >
              {isEmpresaGrupo && (
                <FormGroup className="w-75">
                  <label className="form-control-label">Empresa / filial</label>
                  <div className="mb-3">
                    <Select
                      placeholder="Selecione a empresa"
                      styles={customStyles}
                      value={empresasConfiguraveis
                        .map((item) => ({
                          value: item.id,
                          label:
                            item.id === empresaSelecionada?.id
                              ? `${item.nome} (grupo)`
                              : item.nome,
                        }))
                        .find(
                          (option) =>
                            Number(option.value) === empresaConfiguracaoId,
                        )}
                      onChange={(item: any) =>
                        setEmpresaConfiguracaoId(item ? item.value : null)
                      }
                      options={empresasConfiguraveis.map((item) => ({
                        value: item.id,
                        label:
                          item.id === empresaSelecionada?.id
                            ? `${item.nome} (grupo)`
                            : item.nome,
                      }))}
                    />
                  </div>
                </FormGroup>
              )}

              {podeConfigurarDistribuicao && (
                <FormGroup className="w-75">
                  <label className="form-control-label">
                    Distribuição de Leads
                  </label>
                  <div className="custom-control custom-checkbox mb-3">
                    <input
                      id="check-distribuir-leads-form"
                      className="custom-control-input"
                      type="checkbox"
                      checked={distribuirLeads}
                      onChange={() =>
                        alterarDistribuicaoDaForm(!distribuirLeads)
                      }
                    />
                    <label
                      className="custom-control-label"
                      htmlFor="check-distribuir-leads-form"
                    >
                      Distribuir leads deste formulário entre as filiais
                    </label>
                  </div>
                  <small className="text-muted">
                    As campanhas criadas automaticamente por este formulário já
                    nascem com a distribuição ativada.
                  </small>
                </FormGroup>
              )}

              <FormGroup className="w-75">
                <label className="form-control-label">Funil</label>
                <div className="mb-3">
                  <Select
                    placeholder="Selecione o Funil"
                    styles={customStyles}
                    isClearable
                    value={
                      funis
                        .map((item) => ({ value: item.id, label: item.nome }))
                        .find((o) => String(o.value) === String(funilId)) ?? null
                    }
                    onChange={(item: any) =>
                      alterarFunilDaForm(item ? item.value : null)
                    }
                    options={funis.map((item) => ({
                      value: item.id,
                      label: item.nome,
                    }))}
                  />
                </div>
              </FormGroup>

              <FormGroup className="w-75">
                <label className="form-control-label">Equipe</label>
                <div className="mb-3">
                  <Select
                    placeholder="Selecione a Equipe"
                    styles={customStyles}
                    onChange={(item) => setEquipeSelecionada(item as any)}
                    value={equipeSelecionada}
                    options={equipeOptions?.map((item) => ({
                      value: item.id,
                      label: item.nome,
                    }))}
                  />
                </div>
              </FormGroup>
              <div>
                <Button
                  color="primary"
                  type="button"
                  outline
                  onClick={addEquipeToForm}
                  className="btn-icon btn-3"
                  disabled={isLoading}
                >
                  <span className="btn-inner--icon">
                    <i className="ni ni-fat-add"></i>
                  </span>
                  <span className="btn-inner--text">Adicionar</span>
                </Button>
              </div>
            </Col>
          </Row>
          <Row>
            <Col className="py-4 table-responsive">
              <ToolkitProvider
                data={equipesDaForm}
                keyField="equipe_captacao_id"
                // @ts-ignore
                columns={columns}
                search
              >
                {(props) => (
                  <div className="py-4 custom-table-height">
                    <BootstrapTable
                      {...props.baseProps}
                      bootstrap4={true}
                      bordered={false}
                    />
                  </div>
                )}
              </ToolkitProvider>
            </Col>
          </Row>
        </div>
        <div className="modal-footer">
          <Row>
            <Col>
              <div className="float-right">
                <Button
                  className="ml-auto"
                  color="link"
                  data-dismiss="modal"
                  type="button"
                  onClick={close}
                >
                  Fechar
                </Button>
              </div>
            </Col>
          </Row>
        </div>
      </Modal>
    </>
  );
}
